import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, Slider, TextField, Typography } from "@mui/material";
import { colors } from "../../../theme/muiColors";
import { useDispatch, useSelector } from "react-redux";
import { Add, AttachMoney, Delete } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TuneIcon from "@mui/icons-material/Tune";
import CropIcon from "@mui/icons-material/Crop";
import { decodeToken, getCompanyId, JobFrequencyList, menuPropsModal, StaffTypes } from "../../../utils";
import { useEffect, useState } from "react";
import { CommercialServices } from "./CommercialServices";

import { SuppliesProvider } from "../components/SuppliesProvider";
import { useCalculator } from "../hooks/useCalculator";
import { updateCommercialConfiguration, updateCommercialMileage, updateCommercialServiceById } from "../../../redux/features/calculatorSlice";
import { PlacesAutocomplete } from "../residential/PlacesAutoComplete";
import dayjs from "dayjs";
import { useAppSelector } from "../../../redux/hooks/redux";
import { selectCurrentCompany } from "../../../redux/features/user/selectors/userState.selectors";

const operationExpenses = [
  { label: "Benefits %", name: "benefits", value: 0, required: false },
  { label: "Work Compensation %", name: "workCompensation", value: 0, required: false },
  { label: "Tax %", name: "tax", value: true, required: false },
];

const serviceFields = [
  { label: "Gross Profit %", name: "grossProfit", required: true },
  { label: "Average People", name: "averagePeople", required: false },
];

export const CommercialCalculator = ({ sx, handleCommercialQuote, handleCommercialTab }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  //const commercial = useSelector((state) => state.calculatorState.calculator.commercial);
  const { selectedServices, configuration } = useSelector((state) => state.calculatorState.calculator?.commercial);

  const [destinationCoords, setDestinationCoords] = useState({ lat: "", lng: "" });

  const [allSuppliesByJob, setAllSuppliesByJob] = useState({});
  const { getMyLocations, locations, mileageDistance, getDistanceForMileage, getCalculatorResult } = useCalculator();

  const token = localStorage.getItem('token')
  const company = useAppSelector(selectCurrentCompany)
  const companyId = company?.id


  const handleMileage = async () => {
    const distance = await getDistanceForMileage(configuration.mileage.location, destinationCoords);

    dispatch(
      updateCommercialMileage({
        mileageDistance: distance,
      })
    );
  };

  const handleLatAndLngSelect = (lat, lng) => {
    setDestinationCoords({ lat, lng });
    dispatch(
      updateCommercialMileage({
        latitude: lat,
        longitude: lng,
      })
    );
  };

  const handleSetAllSuppliesForJob = (serviceId, selectedSuppliesArray) => {
    dispatch(
      updateCommercialServiceById({
        id: serviceId,
        service: {
          supplies: selectedSuppliesArray.map((supply) => supply.id),
        },
      })
    );
  };

  const handleChangeStaffType = (index, staffIndex, e) => {
    handleChangeStaffHours(index, staffIndex, e.target.name, e.target.value);
  };

  const handleChangeStaffHours = (index, staffIndex, name, value) => {
    //const { name, value } = e.target;
    const id = selectedServices[index]?.workServiceId;
    const updated = [...selectedServices];
    const job = { ...updated[index] };
    const staffHours = [...job?.staffHours];
    const staffMember = { ...staffHours[staffIndex], [name]: value };
    staffHours[staffIndex] = staffMember;
    job.staffHours = staffHours;
    updated[index] = job;
    dispatch(
      updateCommercialServiceById({
        id,
        service: {
          staffHours: staffHours,
        },
      })
    );
  };

  const getAvailableOptions = (currentIndex) => {
    const selectedTypes = StaffTypes.map((item, idx) => (idx !== currentIndex ? item.staffType : null)).filter(Boolean);
    return StaffTypes.filter((type) => !selectedTypes.includes(type.value));
  };

  const handleAddStaffHours = (id) => {
    const currStaff = selectedServices?.find((service) => service?.workServiceId === id)?.staffHours || [];
    const newStaff = [{ type: "", quantity: "", hoursPerStaff: "" }, ...currStaff];
    dispatch(
      updateCommercialServiceById({
        id,
        service: {
          staffHours: newStaff,
        },
      })
    );
  };

  const handleDeleteStaffHours = (id, staffIndex) => {
    const currStaff = selectedServices?.find((service) => service?.workServiceId === id)?.staffHours;
    if (!currStaff) return;
    const updatedStaff = currStaff.filter((_, idx) => idx !== staffIndex);
    dispatch(
      updateCommercialServiceById({
        id,
        service: {
          staffHours: updatedStaff,
        },
      })
    );
  };

  const handleStaffRates = (value, index) => {
    const staffRates = [...configuration?.staffRatePerHour];
    staffRates[index] = {
      ...staffRates[index],
      rate: value,
    };
    dispatch(updateCommercialConfiguration({ staffRatePerHour: staffRates }));
  };

  const handleStaffRateTypes = () => {
    let staffRates = [...configuration?.staffRatePerHour];
    StaffTypes.forEach((item) => {
      const hasStaffHours = selectedServices?.some((service) => service?.staffHours?.some((staff) => staff?.type === item.value));
      const staffRateIndex = staffRates?.findIndex((staff) => staff?.type === item.value);
      if (hasStaffHours && staffRateIndex === -1) {
        staffRates.push({ type: item.value, rate: "" });
      }
      if (!hasStaffHours && staffRateIndex !== -1) {
        staffRates.splice(staffRateIndex, 1);
      }
    });
    dispatch(updateCommercialConfiguration({ staffRatePerHour: staffRates }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateCommercialConfiguration({ [name]: value }));
  };

  const handleMileageChange = (e) => {
    const { name, type, checked, value } = e.target;
    const val = type === "checkbox" ? checked : value;
    dispatch(updateCommercialMileage({ [name]: val }));
  };

  useEffect(() => {
    getMyLocations();
  }, []);

  /*
  useEffect(() => {
    const payload = mapFormToCommercialState(allFields);
    dispatch(updateCommercialConfiguration(payload));
  }, [allFields]);
  */

  useEffect(() => {
    handleStaffRateTypes();
  }, [selectedServices]);

  const handleCalculate = async () => {
    try {
      const workScopeServicesObj = selectedServices?.map((service, index) => ({
        workServiceId: service?.workServices?.id,
        squareFt: service?.squareFt,
        quoteWorkScopeServices: {
          supplies: service?.supplies,
          mileageReimbursement:
            index === 0 && configuration?.hasMileage
              ? {
                rate: Number(configuration?.mileageRate),
                distance: configuration?.mileageDistance,
              }
              : null,
        },
        staffHours: service?.staffHours,
      }));

      const quoteParams = {
        workTypeId: 1,
        grossProfit: Number(configuration?.grossProfit),
        frequencyOverride: {
          type: "ONCE",
          interval: Number(configuration?.interval),
          duration: Number(configuration?.duration),
        },
        operationExpenses: {
          benefits: Number(configuration.benefits),
          workCompensation: Number(configuration?.workCompensation),
          tax: Number(configuration?.tax),
        },
        staffRatePerHour: configuration?.staffRatePerHour,
        workScopeServices: workScopeServicesObj,
        averagePeople: Number(configuration?.averagePeople),
        contractOverride: configuration?.contractOverride?.map((item) => ({
          startDate: item?.startDate,
          endDate: item?.endDate,
          hours: item?.hours.map((hour) => ({
            startHour: dayjs(hour?.startHour).format("HH:mm"),
            endHour: dayjs(hour?.endHour).format("HH:mm"),
          })),
        })),
      };

      const response = await getCalculatorResult("commercial", quoteParams, companyId);
      handleCommercialQuote(response);
      handleCommercialTab(1);
    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setIsSubmitting(false); // reactiva si fue exitoso o falló
    }
  };

  return (
    <Box sx={{ p: 3, pt: 0, width: "100%" }}>
      <Box sx={{ width: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)", p: 4 }}>
        <Typography variant="subtitle1" textAlign="center">
          Commercial Calculator
        </Typography>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Operation Expenses</Typography>
        <Grid container spacing={2} mt={1}>
          {operationExpenses.map(({ label, name, required }) => (
            <Grid item xs={12} sm={4} key={name}>
              <TextField
                label={label}
                name={name}
                required={required}
                type="number"
                value={configuration[name]}
                fullWidth
                size="small"
                onChange={handleInputChange}
              />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ width: "100%", display: "none", gap: 2, mt: 1 }}>
          <Box sx={{ width: "100%" }}>
            <Typography fontSize={14} mt={1}>
              Contract details
            </Typography>
            <FormControl component="div" sx={{ display: "flex", flexDirection: "row", gap: 1, mt: 1 }}>
              {/* Frequency */}

              <FormControl fullWidth size="small" required>
                <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>Frequency</InputLabel>
                <Select
                  name="frequency"
                  MenuProps={menuPropsModal}
                  variant="outlined"
                  label="Frequency"
                  size="small"
                  required
                  value={configuration.frequency}
                  onChange={handleInputChange}
                >
                  {JobFrequencyList.map((item, key) => (
                    <MenuItem key={key} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Interval—only if frequency !== "ONCE" */}
              {configuration.frequency !== "ONCE" && (
                <>
                  <TextField
                    value={configuration.interval}
                    name="interval"
                    inputProps={{ min: 1 }}
                    size="small"
                    label="Interval"
                    type="number"
                    required
                    fullWidth
                    onChange={handleInputChange}
                  />
                  <TextField
                    name="duration"
                    value={configuration.duration}
                    inputProps={{ min: 1 }}
                    size="small"
                    label={`Duration (${configuration.frequency?.toLowerCase()}s)`}
                    type="number"
                    required
                    fullWidth
                    onChange={handleInputChange}
                  />
                </>
              )}
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Service</Typography>
        <Grid container spacing={2} mt={1}>
          {serviceFields.map(({ label, name, required }) => (
            <Grid item xs={12} sm={4} key={name}>
              <TextField
                name={name}
                label={label}
                value={configuration[name]}
                required={required}
                type="number"
                fullWidth
                size="small"
                onChange={handleInputChange}
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ width: "100%", marginTop: 2 }}>
          <Typography sx={{ padding: 2 }} variant="h5">
            Services
          </Typography>
          <CommercialServices />
        </Box>

        {selectedServices?.length > 0 && (
          <Box>
            <Typography fontSize={17} variant="h4" sx={{ mb: 2, color: colors.blueGrey[800], mt: 4 }}>
              {" "}
              Configure job services
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
                alignItems: "center",
              }}
            >
              {selectedServices?.map((service, index) => (
                <Accordion
                  defaultExpanded
                  key={index}
                  sx={{
                    width: "100%",
                    bgcolor: colors.grey[100],
                    borderRadius: 2,
                    mb: 1,
                    mt: 1,
                  }}
                >
                  <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                    <TuneIcon sx={{ color: colors.blueGrey[600], mr: 1 }} />
                    <Typography sx={{ color: colors.blueGrey[600] }} variant="body1">
                      {service?.workServices?.name}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ display: "flex", flexDirection: "column" }}>
                    <Divider sx={{ width: "100%", mt: -3, mb: 2 }} />
                    <Typography sx={{ color: colors.blueGrey[700] }} component="span">
                      {service?.workService?.description}
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12} md={3} display="flex" alignItems="center">
                        <CropIcon sx={{ color: colors.blue[900] }} />
                        <Typography sx={{ ml: 1 }} color={colors.blue[900]}>
                          Square ft.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={9}>
                        <Slider
                          min={1}
                          max={10000}
                          onChangeCommitted={(event, value) => {
                            if (value === 0) return;
                            dispatch(
                              updateCommercialServiceById({
                                id: service?.workServiceId,
                                service: {
                                  squareFt: value || undefined,
                                },
                              })
                            );
                          }}
                          sx={{ color: colors.blue[300] }}
                          valueLabelDisplay="on"
                          defaultValue={service?.squareFt}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <SuppliesProvider
                          jobServiceId={service.id}
                          jobServiceName={service.workServices.name}
                          selectedSupplies={allSuppliesByJob[service.workServices.name] || []}
                          setAllSuppliesForJob={handleSetAllSuppliesForJob}
                        />

                        <Typography fontSize={14}>Workforce</Typography>

                        {service?.staffHours?.map((item, staffIndex) => (
                          <Grid container spacing={2} key={staffIndex} alignItems="center" sx={{ mt: 1, mb: 1 }}>
                            <Grid item xs={12} md={10}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: { xs: "column", sm: "row" },
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                <FormControl fullWidth size="small" required>
                                  <InputLabel>Worker type</InputLabel>
                                  <Select
                                    MenuProps={menuPropsModal}
                                    label="Worker type"
                                    name="type"
                                    value={item.type}
                                    onChange={(e) => handleChangeStaffType(index, staffIndex, e)}
                                  >
                                    {getAvailableOptions(index)?.map((type, key) => {
                                      const isSelectedByOther = service.staffHours.some((s, i) => i !== staffIndex && s.type === type.value);

                                      return (
                                        <MenuItem key={key} value={type.value} disabled={isSelectedByOther}>
                                          {type.label}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>

                                <TextField
                                  inputProps={{ min: 1 }}
                                  required
                                  size="small"
                                  type="number"
                                  label="Total staff"
                                  name="quantity"
                                  value={item.quantity}
                                  fullWidth
                                  onChange={({ target: { name, value } }) =>
                                    handleChangeStaffHours(index, staffIndex, name, value ? Number(value) : value)
                                  }
                                />

                                <TextField
                                  inputProps={{ min: 1 }}
                                  required
                                  size="small"
                                  label="Hours"
                                  type="number"
                                  name="hoursPerStaff"
                                  value={item.hoursPerStaff}
                                  fullWidth
                                  onChange={({ target: { name, value } }) =>
                                    handleChangeStaffHours(index, staffIndex, name, value ? Number(value) : value)
                                  }
                                />
                              </Box>
                            </Grid>

                            <Grid item xs={12} md={2}>
                              <IconButton
                                disabled={staffIndex === service?.staffHours?.length - 1 && service?.staffHours?.length >= StaffTypes.length}
                                onClick={
                                  staffIndex === service?.staffHours?.length - 1
                                    ? () => handleAddStaffHours(service?.workServiceId)
                                    : () => handleDeleteStaffHours(service?.workServiceId, staffIndex)
                                }
                              >
                                {staffIndex === service?.staffHours?.length - 1 ? (
                                  <>
                                    <Add color="primary" />
                                    <Typography color="primary">Add</Typography>
                                  </>
                                ) : (
                                  <Delete color="primary" />
                                )}
                              </IconButton>
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}

        <Divider />
        <Typography fontSize={14} sx={{ mb: 2, color: colors.blueGrey[800], mt: 3, width: "100%" }}>
          Staff Rates
        </Typography>
        <Box sx={{ display: "flex", alignContent: "initial", gap: 1, width: "100%" }}>
          {configuration?.staffRatePerHour?.map((staff, index) => (
            <TextField
              required
              key={index}
              label={`${staff?.type} rate/hour`}
              variant="outlined"
              type="number"
              onChange={({ target: { value } }) => handleStaffRates(value ? Number(value) : value, index)}
              value={staff.rate}
              inputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney></AttachMoney>
                  </InputAdornment>
                ),
                min: 1,
              }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Mileage</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="hasMileage"
                  checked={configuration.mileage.hasMileage}
                  onChange={handleMileageChange}
                />
              }
              label="Enable Mileage"
            />
          </Grid>

          {configuration.mileage.hasMileage && (
            <>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={500} mb={1}>
                  My Location
                </Typography>

                <FormControl fullWidth size="small" required>
                  <Select
                    MenuProps={menuPropsModal}
                    name="location"
                    required
                    value={configuration.mileage.location}
                    displayEmpty
                    onChange={(e) => dispatch(updateCommercialMileage({ location: e.target.value }))} // ← agrega esto
                    renderValue={(selected) =>
                      selected?.name ? `${selected.name} — ${selected.city || "Unknown"}` : "Select a location"
                    }
                  >
                    {locations?.map((location) => (
                      <MenuItem key={location.id} value={location}>
                        <Box>
                          <Typography fontWeight={500}>{location.name || "Unnamed location"}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {location.city} {location.address}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={500} mb={1}>
                  Destination Address
                </Typography>
                <PlacesAutocomplete
                  handleMileage={handleMileage}
                  value={configuration.mileage.address}
                  handleLatAndLngSelect={handleLatAndLngSelect}
                  onChange={(val) => dispatch(updateCommercialMileage({ address: val }))}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ justifyContent: "center", display: "flex" }}>
          <Button onClick={handleCalculate} variant="contained" sx={{ mt: 4, bgcolor: "primary.main", borderRadius: 3 }}>
            Calculate
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
